import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";

const EXTERNAL = {
  facebook:   "https://www.facebook.com/profile.php?id=61592635580505",
  airbnb:     "https://www.airbnb.com/rooms/1733297737771010926",
  booking:    "https://www.booking.com/hotel/gr/ithaca-garden-villa-kioni.en-gb.html",
  googlemaps: "https://maps.app.goo.gl/nD6p9jA3wSg3LUkWA",
  instagram:  "https://instagram.com/ithacagardenvilla",
  youtube:    "https://www.youtube.com/@IthacaGardenVIlla",
};

// Editorial monoline logo set — visually consistent, brand-recognisable without
// hotlinking third-party trademarked artwork. Each is 24×24 currentColor SVG.
const LogoIcon = ({ name }) => {
  const map = {
    instagram: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4">
        <rect x="3" y="3" width="18" height="18" rx="5"/>
        <circle cx="12" cy="12" r="4"/>
        <circle cx="17.5" cy="6.5" r="1" fill="currentColor"/>
      </svg>
    ),
    facebook: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M13.5 21v-8h2.7l.4-3.3h-3.1V7.6c0-1 .3-1.6 1.6-1.6h1.7V3.1c-.3 0-1.3-.1-2.5-.1-2.5 0-4.2 1.5-4.2 4.3v2.4H7.4V13h2.7v8z"/>
      </svg>
    ),
    // Airbnb "belo" mark — simplified single-path silhouette
    airbnb: (
      <svg width="24" height="24" viewBox="0 0 32 32" fill="currentColor" aria-hidden="true">
        <path d="M16 3.5c-2.2 0-4 1.4-5 3.4L3.4 22.3c-.7 1.6-.9 2.9-.5 4 .6 1.7 2.2 2.7 4.2 2.7 1.6 0 3.3-.7 5.2-2.4C13.7 27.7 15 28.5 16 28.5s2.3-.8 3.7-2c1.9 1.8 3.6 2.5 5.2 2.5 2 0 3.6-1 4.2-2.7.4-1.1.2-2.4-.5-4L21 6.9c-1-2-2.8-3.4-5-3.4Zm0 3.2c1 0 1.8.6 2.3 1.5l7.4 15.1c.4.9.5 1.7.3 2.2-.2.6-.9 1-1.9 1-1 0-2.3-.6-4.1-2.4-.4-.5-.4-1.3.1-2.1.9-1.4 1.5-2.7 1.5-4 0-2.9-2.2-5.1-5.1-5.1s-5.1 2.2-5.1 5.1c0 1.3.5 2.6 1.5 4 .5.8.5 1.6.1 2.1C7.2 25.9 5.9 26.5 4.9 26.5c-1 0-1.7-.4-1.9-1-.2-.5-.1-1.3.3-2.2L10.7 8.2c.5-.9 1.3-1.5 2.3-1.5H16Zm0 8.6c1.2 0 2.1.9 2.1 2.1 0 1-.6 2.2-1.3 3.2-.3.4-.4.5-.8.5s-.5-.1-.8-.5c-.7-1-1.3-2.2-1.3-3.2 0-1.2.9-2.1 2.1-2.1Z"/>
      </svg>
    ),
    // Booking.com — stylised "B."
    booking: (
      <svg width="24" height="24" viewBox="0 0 32 32" fill="currentColor" aria-hidden="true">
        <path d="M6 5.6h9.2c4.1 0 6.5 2.2 6.5 5.4 0 1.9-1 3.4-2.6 4.1v.1c2.2.6 3.6 2.5 3.6 4.9 0 3.6-2.7 6.3-6.9 6.3H6V5.6Zm3.6 8.5h4.8c1.6 0 2.7-1 2.7-2.5s-1.1-2.5-2.7-2.5H9.6v5Zm0 8.9h5.4c1.9 0 3.1-1.1 3.1-2.8 0-1.7-1.2-2.8-3.1-2.8H9.6v5.6Zm14.5 1a2 2 0 1 1 4 0 2 2 0 0 1-4 0Z"/>
      </svg>
    ),
    // Google Maps — pin
    googlemaps: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 21s-7-6.4-7-11a7 7 0 1 1 14 0c0 4.6-7 11-7 11Z"/>
        <circle cx="12" cy="10" r="2.6"/>
      </svg>
    ),
    // YouTube — rounded square with play triangle
    youtube: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M21.6 7.2c-.2-1-1-1.8-2-2C17.8 5 12 5 12 5s-5.8 0-7.6.2c-1 .2-1.8 1-2 2C2.2 9 2.2 12 2.2 12s0 3 .2 4.8c.2 1 1 1.8 2 2C6.2 19 12 19 12 19s5.8 0 7.6-.2c1-.2 1.8-1 2-2 .2-1.8.2-4.8.2-4.8s0-3-.2-4.8ZM10 15V9l5.2 3L10 15Z"/>
      </svg>
    ),
  };
  return map[name] || null;
};

const ELSEWHERE = [
  { key: "instagram",  label: "Instagram" },
  { key: "facebook",   label: "Facebook" },
  { key: "youtube",    label: "YouTube channel" },
  { key: "airbnb",     label: "Airbnb listing" },
  { key: "booking",    label: "Booking.com listing" },
  { key: "googlemaps", label: "Google Maps directions" },
];

export default function Footer() {
  const { t } = useI18n();
  return (
    <footer className="border-t border-soft_border bg-stone_white mt-24">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <img src="/brand/logo-master-transparent.png" alt="Ithaca Garden Villa" className="h-40 w-auto -ml-2" />
          <p className="mt-4 text-ionian/70 max-w-md leading-relaxed">{t("footer.tagline")}</p>
        </div>
        <div>
          <div className="overline mb-4">{t("footer.contact")}</div>
          <p className="text-sm text-ionian/70">{t("footer.location_val")}<br/>{t("footer.location_country")}</p>
          <p className="text-sm text-ionian/70 mt-3">
            <a href="mailto:bookings@ithacagardenvilla.com" className="hover:text-ionian transition">
              bookings@ithacagardenvilla.com
            </a>
          </p>
          <p className="text-sm text-ionian/70 mt-1">
            <a href="tel:+302130491039" data-testid="footer-phone" className="hover:text-ionian transition">
              +30 213 049 1039
            </a>
          </p>
        </div>
        <div>
          <div className="overline mb-4">{t("footer.discover")}</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/" className="text-ionian/70 hover:text-ionian">{t("footer.home")}</Link></li>
            <li><Link to="/booking" className="text-ionian/70 hover:text-ionian">{t("footer.reserve")}</Link></li>
            <li><Link to="/guests" data-testid="footer-guest-portal" className="text-ionian/70 hover:text-ionian">{t("footer.guest_portal")}</Link></li>
            <li><Link to="/admin/login" data-testid="footer-admin-link" className="text-ionian/70 hover:text-ionian">{t("footer.owner_login")}</Link></li>
          </ul>
          <div className="overline mt-8 mb-4">{t("footer.elsewhere")}</div>
          <ul className="flex items-center gap-5 flex-wrap" data-testid="footer-elsewhere">
            {ELSEWHERE.map(({ key, label }) => (
              <li key={key}>
                <a
                  href={EXTERNAL[key]}
                  target="_blank"
                  rel="noreferrer noopener"
                  aria-label={label}
                  title={label}
                  data-testid={`footer-${key}`}
                  className="text-ionian/60 hover:text-brass transition-colors inline-flex"
                >
                  <LogoIcon name={key} />
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="border-t border-soft_border">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-6 flex flex-col md:flex-row justify-between items-start md:items-center text-xs text-ionian/50">
          <div>© {new Date().getFullYear()} Ithaca Garden Villa. {t("footer.rights")}</div>
          <div className="mt-2 md:mt-0 tracking-widest uppercase">{t("footer.subtitle")}</div>
        </div>
      </div>
    </footer>
  );
}
